import { ErrorBanner } from './ErrorBanner.js';
import { LoadingStatus } from './LoadingStatus.js';
import { TrackFlagNote } from './TrackFlagNote.js';
import { TrackRow } from './TrackRow.js';

import type { ChannelId, SearchResultTrack } from '@mixwire/contracts';
import type { ReactElement, ReactNode } from 'react';

type TrackSearchRowProps = {
  readonly track: SearchResultTrack;
  readonly myChannelId: ChannelId | undefined;
  readonly actionLabel: string;
  readonly isActionDisabled: boolean;
  readonly onAction: () => void;
};

const TrackSearchRow = ({
  track,
  myChannelId,
  actionLabel,
  isActionDisabled,
  onAction,
}: TrackSearchRowProps): ReactElement => (
  <li
    className={`rounded-control flex min-w-0 flex-col px-2 py-2.5 ${
      track.isAlreadySuggested ? 'bg-surface-sunken' : 'hover:bg-surface-hover'
    }`}
  >
    <div className="flex min-w-0 items-center gap-3 sm:gap-3.5">
      <TrackRow
        title={track.title}
        artist={track.artist}
        colour={undefined}
        imageUrl={track.artUrl}
        titleClassName="text-content truncate text-sm font-semibold"
        artistClassName="text-secondary truncate text-xs"
      />
      {track.suggestedCount === 0 ? null : (
        <span className="text-muted shrink-0 rounded-pill px-2 py-1 text-xs font-medium">
          ×{track.suggestedCount}
        </span>
      )}
      <button
        type="button"
        disabled={isActionDisabled}
        onClick={onAction}
        className="border-line text-content hover:bg-surface-hover shrink-0 rounded-pill border px-3.5 py-2.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50"
      >
        {actionLabel}
      </button>
    </div>

    <TrackFlagNote item={track} myChannelId={myChannelId} />
  </li>
);

export type TrackSearchProps = {
  readonly query: string;
  readonly onQueryChange: (query: string) => void;
  /** The settled term the results belong to — the live `query` is still being typed. */
  readonly term: string;
  readonly results: readonly SearchResultTrack[];
  /** The DJ's own channel, so a clash there reads as theirs. The suggester holds none and says so. */
  readonly myChannelId?: ChannelId | undefined;
  readonly isSearching: boolean;
  readonly failureMessage: string | undefined;
  readonly inputLabel: string;
  readonly emptyQueryContent: ReactNode;
  readonly actionLabel: (track: SearchResultTrack) => string;
  readonly isActionDisabled: (track: SearchResultTrack) => boolean;
  readonly onAction: (track: SearchResultTrack) => void;
  readonly onActivity?: (() => void) | undefined;
  /**
   * Layout for the block below the input. A surface with a viewport to fill wants the default,
   * which scrolls; one whose panel should grow with its content wants to say so, because a
   * `flex-1` box inside a short row collapses to a few centimetres of scroller.
   */
  readonly listClassName?: string | undefined;
};

export const TrackSearch = ({
  query,
  onQueryChange,
  term,
  results,
  myChannelId,
  isSearching,
  failureMessage,
  inputLabel,
  emptyQueryContent,
  actionLabel,
  isActionDisabled,
  onAction,
  onActivity,
  listClassName = 'flex-1 overflow-y-auto px-2 pb-2 sm:px-4',
}: TrackSearchProps): ReactElement => (
  <>
    <div className="px-4 pb-4 sm:px-6">
      <input
        value={query}
        onChange={(event) => {
          onActivity?.();
          onQueryChange(event.target.value);
        }}
        placeholder={inputLabel}
        aria-label={inputLabel}
        /* text-base on a phone, not text-sm: iOS Safari zooms the page in when a focused input's text is under 16px, and never zooms back out. */
        className="border-line text-content placeholder:text-muted focus:outline-accent-ring w-full rounded-pill border px-4 py-3 text-base focus:outline-2 focus:outline-offset-2 sm:px-5 sm:text-sm"
      />
    </div>

    <div
      onScroll={() => {
        onActivity?.();
      }}
      className={listClassName}
    >
      {failureMessage === undefined ? null : (
        <ErrorBanner className="rounded-control mx-2 mb-2 px-3 py-2">{failureMessage}</ErrorBanner>
      )}

      {term === '' ? emptyQueryContent : null}

      {isSearching ? <LoadingStatus className="py-10">Searching Spotify…</LoadingStatus> : null}

      {!isSearching && failureMessage === undefined && term !== '' && results.length === 0 ? (
        <p className="text-muted py-10 text-center text-sm break-words">
          Nothing on Spotify matching “{term}”.
        </p>
      ) : null}

      <ul aria-label="Search results" className="flex list-none flex-col p-0">
        {results.map((track) => (
          <TrackSearchRow
            key={track.id}
            track={track}
            myChannelId={myChannelId}
            actionLabel={actionLabel(track)}
            isActionDisabled={isActionDisabled(track)}
            onAction={() => {
              onAction(track);
            }}
          />
        ))}
      </ul>
    </div>
  </>
);
